import { useEffect, useMemo, useState } from 'react';

import { getListPitstop, Pitstop } from '@/services/api/pitstops';
import type { Shipment } from '@/services/api/shipments';

export interface SetHubOption {
  value: number;
  label: string;
  group: string;
}

/**
 * The hubs/pitstops "Set hub" may pick for these shipments: any Dash hub,
 * plus each shipment client's own pitstops (fetched while `active`). A
 * pitstop of another client is never offered — the BE refuses it anyway.
 */
export const useSetHubOptions = (
  shipments: Pick<Shipment, 'clientId' | 'client'>[],
  hubs: Pitstop[],
  active: boolean
): { options: SetHubOption[]; pitstops: Pitstop[]; loading: boolean } => {
  const [clientPitstops, setClientPitstops] = useState<Pitstop[]>([]);
  const [loading, setLoading] = useState(false);

  // The selection's clients (usually one) — their own pitstops are offered.
  const clientKey = shipments
    .map((s) => `${s.clientId}:${s.client?.name ?? ''}`)
    .join('|');
  const clients = useMemo(() => {
    const byId = new Map<number, string>();
    shipments.forEach((s) =>
      byId.set(s.clientId, s.client?.name ?? `Client ${s.clientId}`)
    );
    return [...byId.entries()];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientKey]);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    setLoading(true);
    Promise.all(
      clients.map(([clientId]) =>
        getListPitstop({
          type: 'PROVIDER_WAREHOUSE',
          providerId: clientId,
          page: '1',
          size: '100',
        })
          .then((res) => res.data ?? [])
          .catch(() => [] as Pitstop[])
      )
    )
      .then((lists) => {
        if (!cancelled) setClientPitstops(lists.flat());
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [active, clients]);

  const ownPitstops = useMemo(() => {
    const clientName = new Map(clients);
    return clientPitstops
      .filter((p) => p.provider?.id != null && clientName.has(p.provider.id))
      .sort((a, b) => (a.provider?.id ?? 0) - (b.provider?.id ?? 0));
  }, [clientPitstops, clients]);

  const options = useMemo(() => {
    const clientName = new Map(clients);
    return [
      ...hubs.map((h) => ({ value: h.id, label: h.name, group: 'Hub Dash' })),
      ...ownPitstops.map((p) => ({
        value: p.id,
        label: p.name,
        group: `Pitstop client · ${clientName.get(p.provider?.id ?? 0)}`,
      })),
    ];
  }, [hubs, ownPitstops, clients]);

  return {
    options,
    pitstops: useMemo(() => [...hubs, ...ownPitstops], [hubs, ownPitstops]),
    loading,
  };
};
